<template>
  <div>
    <flat-pickr
      :model-value="props.modelValue"
      :config="config"
      @on-close="formatRangeDate"
      @update:modelValue="handleDate"
      :placeholder="props.placeholder"
      ref="datepicker"
      :disabled="props.disabled"
      :class="baseClass"
    />
    <div :class="clearClass" @click="clear" data-clear v-if="props.nullable">
      <clear-icon v-if="canClear"/>
    </div>
  </div>
</template>

<script setup>
  import ClearIcon from '@/components/icons-v2/cross-icon.vue';
  import FlatPickr from 'vue-flatpickr-component';
  import { Lithuanian } from 'flatpickr/dist/l10n/lt.js';
  import { english } from 'flatpickr/dist/l10n/default.js';
  import shortcutButtonsPlugin from 'shortcut-buttons-flatpickr';
  import { useAuthStore } from '@/stores/auth';
  import { watch, ref, computed } from 'vue';
  import { langService } from '@/core';

  const emit = defineEmits(['update:modelValue']);

  const props = defineProps({
    version: {
      type: String,
      default: '1'
    },
    size: {
      type: String,
      default: 'base',
    },
    modelValue: {
      type: [String, Array, Object],
      default: null,
    },
    format: {
      type: String,
      default: 'Y-m-d',
    },
    range: {
      type: Boolean,
      default: null,
    },
    shortcuts: {
      type: Array,
      default: () => [],
    },
    timePicker: {
      type: Boolean,
      default: null,
    },
    noCalendar: {
      type: Boolean,
      default: false,
    },
    disabledDate: {
      type: null,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: () => langService.trans('extra.select_date'),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    nullable: {
      type: Boolean,
      default: true,
    },
    allowInput: {
      type: Boolean,
      default: false,
    },
    shortcutStartingDate: {
      type: String,
      default: null,
    },
    static: {
      type: Boolean,
      default: true,
    },
    popupClasses: {
      type: Array,
      default: () => [],
    },
  });

  const config = {
    wrap: true,
    static: props.static,
    position: props.static ? 'below' : 'auto',
    allowInput: props.allowInput,
    enableTime: props.timePicker,
    noCalendar: props.noCalendar,
    dateFormat: props.format,
    time_24hr: true,
    mode: props.range ? 'range' : 'single',
    disable: [props.disabledDate],
    locale: '',
    onReady: (_, __, instance) => {
      instance.calendarContainer?.classList.add(...props.popupClasses);
    },
    plugins: props.shortcuts.length > 0 ? [
      shortcutButtonsPlugin({
        button: props.shortcuts.map(s => ({ label: s.text })),
        onClick: (index, fp) => {
          // eslint-disable-next-line no-useless-assignment
          let date = null;
          if (props.shortcutStartingDate) {
            date = new Date(props.shortcutStartingDate);
          } else {
            date = props.modelValue ?? new Date();
          }
          fp.setDate(props.shortcuts[index].date(date));
          fp.close();
        },
      }),
    ] : '',
  };
  const authStore = useAuthStore();
  const datepicker = ref(null);
  const baseClass = computed(() => {
    return [
      props.version === 'v2' ? 'flatpickr-input__custom-v2' : 'flatpickr-input__custom',
      props.version === 'v2' ? `v2-text--${props.size}` : '',
      props.version === 'v2' ? `flatpickr-input__custom-v2--${props.size}` : '',
    ].join(' ');
  });

  const clearClass = computed(() => {
    return [props.version === 'v2' ? 'flatpickr-clear-v2' : 'flatpickr-clear'].join(' ');
  });
  const canClear = computed(() => {
    if (Array.isArray(props.modelValue) && !props.modelValue.includes(null)) {
      return true;
    }

    if (props.modelValue !== null && !props.disabled) {
      return true;
    }

    return false;
  });

  watch(() => authStore.user?.profile?.language, () => {
    const lang = authStore.user?.profile?.language;
    config.locale = lang === 'lt' ? Lithuanian : english;
  }, { immediate: true });

  function redrawCalendar() {
    datepicker.value.fp.redraw();
  }

  function formatRangeDate(selectedDates, dateStr, instance) {
    if (!props.range) {
      return;
    }

    if (selectedDates[0] && selectedDates[1]) {
      const dateStart = instance.formatDate(selectedDates[0], 'Y-m-d');
      const dateEnd = instance.formatDate(selectedDates[1], 'Y-m-d');

      const reformattedRangeDate = [];
      reformattedRangeDate.push(dateStart);
      reformattedRangeDate.push(dateEnd);

      emit('update:modelValue', reformattedRangeDate);
    }
  }

  function handleDate(value) {
    if (props.range) {
      return;
    }

    emit('update:modelValue', value);
  }

  function clear() {
    if (datepicker.value) {
      datepicker.value.fp.clear();
    }

    emit('update:modelValue', null);
  }

  function set(key, value) {
    datepicker.value.fp.set(key, value);
  }

  function open() {
    datepicker.value.fp.open();
  }

  defineExpose({
    redrawCalendar,
    set,
    open,
  });
</script>
